import React, {useMemo, useState} from 'react';
import {ActivityIndicator, Image, Pressable, TextInput, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {activeBadge} from '../../../Hooks/LmsCourseApi/useActivePuzzles';
import {
  courseNote,
  formatWhen,
  Masterclass,
  useMasterclasses,
} from '../../../Hooks/LmsCourseApi/useMasterclasses';
import {CourseJob} from '../../../Hooks/LmsCourseApi/types';

import ActivePuzzlePreview from './ActivePuzzlePreview';
import ActivePuzzlesToggle from './ActivePuzzlesToggle';
import CourseButton from './CourseButton';
import MasterclassPreview from './MasterclassPreview';
import PuzzleIcon from './PuzzleIcon';
import {formatDuration} from '../courseFormat';
import {CARD_SHADOW, F, STATUS} from '../courseTheme';
import {E} from '../editor/editorTheme';
import {focusVisible, ring} from '../editor/ui';

type Props = {
  /** Called with the chosen recordings, in the order they were picked. */
  onBuild: (picked: Masterclass[], title: string) => void;
  busy?: boolean;
  /**
   * A course from launched ("active") puzzles was accepted by the server:
   * follow its job like any other build.
   */
  onActiveStarted?: (job: CourseJob) => void;
};

const PAGE_SIZE = 25;

/** Fixed, so tiles line up in a grid instead of stretching to fill a row. */
const TILE_WIDTH = 260;
/** 16:9 on TILE_WIDTH, the shape every one of these recordings is. */
const POSTER_HEIGHT = Math.round((TILE_WIDTH * 9) / 16);

/** "🧩 11 active puzzles", with a vector icon rather than the emoji. */
function ActiveBadge({kept}: {kept: number}) {
  return (
    <View
      style={{
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: F.tealDark,
        // tealDark on this tint measures 4.84:1.
        backgroundColor: E.selected,
        marginTop: 2,
      }}>
      <PuzzleIcon size={14} color={F.tealDark} />
      <CCText style={{fontSize: 12, fontWeight: '700', color: F.tealDark}}>
        {activeBadge(kept)}
      </CCText>
    </View>
  );
}

/**
 * The poster, or a legible stand-in.
 *
 * Plenty of rows carry no thumbnail, and a gallery that renders a hole for
 * them is worse than the list it replaced. The fallback keeps the tile the
 * same shape and says what the recording is in the only words available.
 */
function MasterclassPoster({item}: {item: Masterclass}) {
  if (item.thumbnail) {
    return (
      <Image
        accessibilityIgnoresInvertColors
        source={{uri: item.thumbnail}}
        style={{width: '100%', height: POSTER_HEIGHT, backgroundColor: F.teal}}
        resizeMode="cover"
      />
    );
  }
  return (
    <View
      style={{
        width: '100%',
        height: POSTER_HEIGHT,
        backgroundColor: 'rgba(61,171,158,0.18)',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
        gap: 6,
      }}>
      <Ionicons name="videocam-outline" size={24} color={F.brown} />
      <CCText
        style={{
          fontSize: 12,
          fontWeight: '600',
          color: F.brown,
          textAlign: 'center',
        }}
        numberOfLines={2}>
        {item.coach || item.class_name || item.category || 'Recording'}
      </CCText>
    </View>
  );
}

export default function MasterclassPicker({
  onBuild,
  busy = false,
  onActiveStarted,
}: Props) {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [title, setTitle] = useState('');
  // Ordered, not a Set: the order recordings are picked is the order they
  // play, and a Set would silently reorder a three-part course.
  const [picked, setPicked] = useState<Masterclass[]>([]);
  // Watching is not choosing: the previewed recording is tracked separately
  // from `picked` so opening one never enrols it.
  const [preview, setPreview] = useState<Masterclass | null>(null);
  // "Active puzzles only": one recording, built from the puzzles its coach
  // launched in class. Kept apart from `picked` so switching back finds the
  // multi-recording selection exactly as it was left.
  const [activeOnly, setActiveOnly] = useState(false);
  const [chosen, setChosen] = useState<Masterclass | null>(null);
  const [showPuzzles, setShowPuzzles] = useState(false);
  const [focusedTile, setFocusedTile] = useState<number | null>(null);

  const list = useMasterclasses({
    month,
    q: search,
    page,
    pageSize: PAGE_SIZE,
    activePuzzles: activeOnly,
  });
  const months = list.data?.months ?? [];
  const results = list.data?.results ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedMonth = month ?? list.data?.month ?? '';

  const pickedIds = useMemo(
    () => new Set(picked.map(item => item.video_course_id)),
    [picked],
  );

  const toggle = (item: Masterclass) => {
    setPicked(current =>
      current.some(p => p.video_course_id === item.video_course_id)
        ? current.filter(p => p.video_course_id !== item.video_course_id)
        : [...current, item],
    );
  };

  const move = (index: number, by: number) => {
    setPicked(current => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const changeMonth = (value: string) => {
    setMonth(value);
    setPage(1);
  };

  const switchMode = (next: boolean) => {
    setActiveOnly(next);
    // A different listing: page 3 of one is not page 3 of the other.
    setPage(1);
    setChosen(null);
    setShowPuzzles(false);
  };

  /** Active mode: choosing is looking — the sheet opens before any build. */
  const choose = (item: Masterclass) => {
    setChosen(item);
    setShowPuzzles(true);
  };

  // Only the first recording anchors the course, so only that one can be
  // refused for already having one.
  const anchorHasCourse = picked.length > 0 && picked[0].course != null;

  return (
    <View
      style={[
        {
          backgroundColor: F.white,
          borderRadius: F.radius.card,
          padding: 24,
          gap: 16,
        },
        CARD_SHADOW,
      ]}>
      {/* Width comes from the screen's column, so this card and the upload
          card below line up edge to edge. */}
      <View style={{gap: 4}}>
        <CCText style={{fontSize: 22, fontWeight: '600', color: F.brownDark}}>
          {activeOnly ? 'Choose a class' : 'Choose masterclasses'}
        </CCText>
        <CCText style={{fontSize: 15, fontWeight: '500', color: F.brown, lineHeight: 22}}>
          {activeOnly
            ? 'Classes where the coach launched at least 3 puzzles, most first. ' +
              'Choose one to see every puzzle before anything is built.'
            : 'Pick one recording, or several to run together as a single ' +
              'course. The order you pick them is the order they play.'}
        </CCText>
      </View>
      <ActivePuzzlesToggle
        value={activeOnly}
        onChange={switchMode}
        disabled={busy}
      />

      {/* --- filters --- */}
      <View style={{flexDirection: 'row', gap: 10, flexWrap: 'wrap', alignItems: 'center'}}>
        {months.length ? (
          <View
            style={{
              borderWidth: 1,
              borderColor: 'rgba(38,35,34,0.2)',
              borderRadius: 8,
              overflow: 'hidden',
            }}>
            {/* A native select: the platform's own month list beats a custom
                dropdown nobody can keyboard through. */}
            {React.createElement('select', {
              value: selectedMonth,
              onChange: (event: any) => changeMonth(event.target.value),
              'aria-label': 'Month',
              style: {
                border: 'none',
                padding: '10px 12px',
                fontSize: 14,
                background: 'transparent',
                color: F.brownDark,
                outline: 'none',
              },
            }, [
              React.createElement('option', {key: 'all', value: 'all'}, 'All months'),
              ...months.map(option =>
                React.createElement(
                  'option', {key: option.value, value: option.value}, option.label,
                ),
              ),
            ])}
          </View>
        ) : null}

        <TextInput
          value={search}
          onChangeText={value => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search title, coach or class"
          accessibilityLabel="Search masterclasses"
          style={{
            flex: 1,
            minWidth: 200,
            borderWidth: 1,
            borderColor: 'rgba(38,35,34,0.2)',
            borderRadius: 8,
            paddingHorizontal: 12,
            paddingVertical: 10,
            fontSize: 14,
          }}
        />
        {list.isFetching ? <ActivityIndicator color={F.teal} /> : null}
      </View>

      {/* --- results --- */}
      {list.isError ? (
        <CCText style={{fontSize: 14, fontWeight: '600', color: STATUS.bad.fg}}>
          {(list.error as any)?.response?.data?.error ??
            'Could not load the masterclasses. Try again.'}
        </CCText>
      ) : null}

      {!list.isLoading && !results.length ? (
        <View
          style={{
            alignItems: 'flex-start',
            gap: 10,
            padding: 16,
            borderRadius: 12,
            backgroundColor: E.canvas,
          }}>
          <CCText style={{fontSize: 14, fontWeight: '500', color: F.brown, lineHeight: 21}}>
            {activeOnly
              ? search
                ? `Nothing with launched puzzles matches “${search}” in this month. Try another month, or clear the search.`
                : 'No recordings in this month have 3 or more launched puzzles. Pick another month from the dropdown.'
              : search
              ? `Nothing matches “${search}” in this month. Try another month, or clear the search.`
              : 'No recordings in this month. Pick another from the dropdown.'}
          </CCText>
          {/* The picker opens on this month, often still empty a few days
              in; one press beats hunting through the dropdown. */}
          {selectedMonth !== 'all' ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Show all months"
              onPress={() => changeMonth('all')}
              style={({hovered, pressed}: any) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                minHeight: 36,
                paddingHorizontal: 12,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: F.tealDark,
                backgroundColor: pressed ? E.pressed : hovered ? E.selected : F.white,
                cursor: 'pointer',
              })}>
              <Ionicons name="calendar-outline" size={16} color={F.tealDark} />
              <CCText style={{fontSize: 14, fontWeight: '600', color: F.tealDark}}>
                Show all months
              </CCText>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      {/* A gallery rather than a list. These rows are titled by Zoom
          ("recording_97346951740_…"), so the poster is usually the only thing
          that says what a recording actually is. */}
      <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 16}}>
        {results.map(item => {
          const isPicked = activeOnly
            ? chosen?.video_course_id === item.video_course_id
            : pickedIds.has(item.video_course_id);
          const order = picked.findIndex(
            p => p.video_course_id === item.video_course_id,
          );
          const note = courseNote(item);
          return (
            <View
              key={item.video_course_id}
              style={{
                width: TILE_WIDTH,
                borderRadius: F.radius.card,
                borderWidth: 1,
                borderColor: isPicked ? F.teal : 'rgba(38,35,34,0.15)',
                backgroundColor: isPicked
                  ? 'rgba(61,171,158,0.08)'
                  : 'rgba(255,255,255,0.65)',
                overflow: 'hidden',
              }}>
              <Pressable
                accessibilityRole={activeOnly ? 'radio' : 'checkbox'}
                accessibilityState={{checked: isPicked}}
                // The web drops accessibilityState; without this a screen
                // reader never hears which tiles are picked.
                {...({'aria-checked': isPicked} as any)}
                accessibilityLabel={item.title}
                accessibilityHint={
                  activeOnly
                    ? 'Shows the puzzles launched in this class'
                    : undefined
                }
                disabled={busy}
                onPress={() => (activeOnly ? choose(item) : toggle(item))}
                onFocus={() =>
                  setFocusedTile(focusVisible() ? item.video_course_id : null)
                }
                onBlur={() => setFocusedTile(null)}
                style={({pressed}: any) => [
                  {
                    opacity: busy ? 0.45 : pressed ? 0.85 : 1,
                    borderRadius: F.radius.card,
                  },
                  ring(focusedTile === item.video_course_id, true),
                ]}>
                <MasterclassPoster item={item} />

                <View style={{padding: 12, gap: 4}}>
                  <CCText
                    style={{fontSize: 15, fontWeight: '600', color: F.brownDark}}
                    numberOfLines={2}>
                    {item.title}
                  </CCText>
                  <CCText style={{fontSize: 12, fontWeight: '500', color: F.brown}}>
                    {[
                      formatWhen(item) +
                        (item.date_is_class_date ? '' : ' (imported)'),
                      // Written as the course list writes it ("1h 37m").
                      formatDuration((item.duration_min || 0) * 60),
                      item.coach,
                      item.class_name || item.category,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </CCText>
                  {activeOnly && item.active_puzzles ? (
                    <ActiveBadge kept={item.active_puzzles.kept} />
                  ) : null}
                  {note ? (
                    <CCText
                      style={{fontSize: 12, fontWeight: '600', color: F.teal}}>
                      {note}
                    </CCText>
                  ) : null}

                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                      marginTop: 2,
                    }}>
                    {item.has_transcript ? (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                        }}>
                        <Ionicons
                          name="document-text-outline"
                          size={14}
                          color={F.brown}
                        />
                        <CCText
                          style={{fontSize: 12, fontWeight: '600', color: F.brown}}>
                          Transcript
                        </CCText>
                      </View>
                    ) : null}
                    {item.is_private ? (
                      <CCText
                        style={{fontSize: 12, fontWeight: '600', color: F.brown}}>
                        Private
                      </CCText>
                    ) : null}
                  </View>
                </View>

                {/* The pick order, not a tick: the order recordings are chosen
                    is the order the parts play. */}
                {isPicked ? (
                  <View
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      minWidth: 24,
                      height: 24,
                      borderRadius: 12,
                      paddingHorizontal: 6,
                      backgroundColor: F.teal,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    {activeOnly ? (
                      <Ionicons name="checkmark" size={14} color={F.white} />
                    ) : (
                      <CCText
                        style={{
                          fontSize: 12,
                          fontWeight: '700',
                          color: F.white,
                        }}>
                        {order + 1}
                      </CCText>
                    )}
                  </View>
                ) : null}
              </Pressable>

              {/* A sibling of the selecting Pressable, not a child. Nesting it
                  would make "watch this" and "put this in the course" the same
                  gesture, and a mis-click there is a wrong course, not a wrong
                  click. Offered only when the row actually signed. */}
              {item.video_url ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Preview ${item.title}`}
                  onPress={() => setPreview(item)}
                  style={({pressed}) => ({
                    position: 'absolute',
                    top: POSTER_HEIGHT / 2 - 22,
                    left: TILE_WIDTH / 2 - 22,
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: pressed
                      ? 'rgba(0,0,0,0.75)'
                      : 'rgba(0,0,0,0.55)',
                  })}>
                  <Ionicons name="play" size={20} color={F.white} />
                </Pressable>
              ) : null}
            </View>
          );
        })}
      </View>

      {preview ? (
        <MasterclassPreview item={preview} onClose={() => setPreview(null)} />
      ) : null}

      {activeOnly && chosen && showPuzzles ? (
        <ActivePuzzlePreview
          item={chosen}
          onClose={() => setShowPuzzles(false)}
          onStarted={job => {
            setShowPuzzles(false);
            onActiveStarted?.(job);
          }}
        />
      ) : null}

      {/* --- paging --- */}
      {pages > 1 ? (
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Previous page"
            disabled={page <= 1 || busy}
            onPress={() => setPage(p => Math.max(1, p - 1))}>
            <Ionicons
              name="chevron-back"
              size={20}
              color={page <= 1 ? 'rgba(38,35,34,0.25)' : F.brown}
            />
          </Pressable>
          <CCText style={{fontSize: 13, fontWeight: '600', color: F.brown}}>
            {`Page ${page} of ${pages} · ${total} recordings`}
          </CCText>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Next page"
            disabled={page >= pages || busy}
            onPress={() => setPage(p => Math.min(pages, p + 1))}>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={page >= pages ? 'rgba(38,35,34,0.25)' : F.brown}
            />
          </Pressable>
        </View>
      ) : null}

      {/* --- selection: active mode is one recording --- */}
      {activeOnly && chosen ? (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: 'rgba(38,35,34,0.12)',
            paddingTop: 14,
            gap: 10,
          }}>
          <CCText style={{fontSize: 13, fontWeight: '700', color: F.brownDark}}>
            One recording selected
          </CCText>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}>
            <CCText
              style={{
                fontSize: 13,
                fontWeight: '500',
                color: F.brown,
                flexShrink: 1,
              }}
              numberOfLines={1}>
              {chosen.title}
            </CCText>
            {chosen.active_puzzles ? (
              <ActiveBadge kept={chosen.active_puzzles.kept} />
            ) : null}
          </View>
          <View style={{alignSelf: 'flex-start'}}>
            <CourseButton
              label="See the puzzles"
              showArrow
              disabled={busy}
              onPress={() => setShowPuzzles(true)}
            />
          </View>
        </View>
      ) : null}

      {/* --- selection --- */}
      {!activeOnly && picked.length ? (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: 'rgba(38,35,34,0.12)',
            paddingTop: 14,
            gap: 10,
          }}>
          <CCText style={{fontSize: 13, fontWeight: '700', color: F.brownDark}}>
            {picked.length === 1
              ? 'One recording selected'
              : `${picked.length} recordings, in this order`}
          </CCText>

          {picked.map((item, index) => (
            <View
              key={item.video_course_id}
              style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
              <CCText style={{fontSize: 12, fontWeight: '700', color: F.teal, width: 18}}>
                {index + 1}
              </CCText>
              <CCText
                style={{fontSize: 13, fontWeight: '500', color: F.brown, flex: 1}}
                numberOfLines={1}>
                {item.title}
              </CCText>
              {picked.length > 1 ? (
                <>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Move ${item.title} earlier`}
                    disabled={index === 0 || busy}
                    onPress={() => move(index, -1)}>
                    <Ionicons
                      name="arrow-up"
                      size={16}
                      color={index === 0 || busy ? 'rgba(38,35,34,0.25)' : F.brown}
                    />
                  </Pressable>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Move ${item.title} later`}
                    disabled={index === picked.length - 1 || busy}
                    onPress={() => move(index, 1)}>
                    <Ionicons
                      name="arrow-down"
                      size={16}
                      color={
                        index === picked.length - 1 || busy
                          ? 'rgba(38,35,34,0.25)'
                          : F.brown
                      }
                    />
                  </Pressable>
                </>
              ) : null}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Remove ${item.title}`}
                disabled={busy}
                onPress={() => toggle(item)}>
                <Ionicons name="close" size={16} color={F.wrongFg} />
              </Pressable>
            </View>
          ))}

          {anchorHasCourse ? (
            <CCText style={{fontSize: 13, fontWeight: '600', color: STATUS.bad.fg, lineHeight: 20}}>
              {`“${picked[0].title}” already has a course. Building will be ` +
                `refused unless you rebuild it — put a different recording ` +
                `first, or open the existing course.`}
            </CCText>
          ) : null}

          <TextInput
            value={title}
            onChangeText={setTitle}
            placeholder={
              picked.length > 1
                ? 'Course title (defaults to the first recording)'
                : 'Course title (optional)'
            }
            accessibilityLabel="Course title"
            style={{
              borderWidth: 1,
              borderColor: 'rgba(38,35,34,0.2)',
              borderRadius: 8,
              padding: 12,
              fontSize: 14,
            }}
          />

          <View style={{flexDirection: 'row', gap: 12, alignItems: 'center'}}>
            <CourseButton
              label={busy ? 'Starting…' : 'Build course'}
              disabled={busy}
              onPress={() => onBuild(picked, title.trim())}
            />
            <CourseButton
              label="Clear"
              variant="review"
              disabled={busy}
              onPress={() => setPicked([])}
            />
          </View>
        </View>
      ) : null}
    </View>
  );
}
